import { useMemo } from 'react';
import { SingleValue } from 'react-select';
import { selectChartSettingsSliceAction } from '../../../../redux/slices/analyses';
import * as ChartGroupsUI from './ChartGroupsUI';
import { generateGroupbyHint } from '../../utils';
import { QueryType } from '../../../../utils/apollo/graphqlSchema';
import { IDisplayedSlice } from '../../interfaces';

import { isNil } from 'lodash';
import { CHART_TYPES } from '../../constants/chartTypes';
import {
  CHART_GROUPS_DEFAULT_COUNT,
  CHART_GROUPS_PATH_MAX_COUNT,
} from '../../constants/utils';
import { useAppDispatch } from '../../../../redux/hooks';
import RenamedIndicator from '../../../RenamedIndicator';
import GroupDropdown from '../GroupDropdown';
import { useResizeDetector } from 'react-resize-detector';
import { assertNotNil } from '../../../../utils/types';
import useBuilderInfoDisplay from '../../utils/useBuilderInfoDisplay';
import { getSliceColorFromColorConfig } from '../../utils/analysisColorConfig';
import useToggleSlice from '../../hooks/useToggleSlice';
import { useChartContext } from '../../ChartContext';
import ChartGroupWrapper from './ChartGroupWrapper';

const getMenuOptions = ({
  sliceKey,
  visibleSlices,
  displayedSlices,
}: {
  sliceKey: string;
  visibleSlices: IDisplayedSlice[];
  displayedSlices: IDisplayedSlice[];
}): IDisplayedSlice[] => {
  return displayedSlices.reduce<IDisplayedSlice[]>((result, slice) => {
    // Add selected slice to the top
    if (sliceKey === slice.key) {
      result.unshift(slice);
    }

    // Only when slice is not visible on UI - add it to result
    if (!visibleSlices.some((visibleSlice) => visibleSlice.key === slice.key)) {
      result.push(slice);
    }

    return result;
  }, []);
};

interface IProps {
  analysisId: string;
}

export default ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();

  const { ref } = useResizeDetector({
    refreshMode: 'debounce',
    refreshRate: 300,
  });

  const {
    metadata: { queryType },
    settings: { chartSettings },
    displayedSlices,
    colorConfig,
  } = useChartContext();

  const { chartType } = chartSettings;
  const topBreakdownsValue = chartSettings?.topBreakdownsValue ?? 0;

  const maxGroupsAllowed =
    chartType === CHART_TYPES.PATH ? CHART_GROUPS_PATH_MAX_COUNT : undefined;
  const chartGroupsContainerId = `ChartGroups-${analysisId}`;

  const toggleSlice = useToggleSlice(analysisId);

  const onGroupChange = (
    option: SingleValue<IDisplayedSlice>,
    sliceKey: string,
  ) => {
    assertNotNil(option);

    dispatch(
      selectChartSettingsSliceAction({
        analysisId,
        sourceSliceKey: sliceKey,
        targetSliceKey: option.key,
      }),
    );
  };

  const getGroupLabelElement = (
    displayedSlice: IDisplayedSlice,
    sliceIndex: number,
  ) => {
    const sliceColor = getSliceColorFromColorConfig(
      colorConfig,
      displayedSlice.key,
    );

    const displayName = displayedSlice.rank
      ? `${displayedSlice.rank}: ${displayedSlice.name}`
      : displayedSlice.name;

    return (
      <ChartGroupsUI.SingleValueLabel
        $isVisible={displayedSlice.isVisible}
        onClick={() =>
          toggleSlice(displayedSlice.key, displayedSlice.isVisible)
        }
      >
        {queryType !== QueryType.Path ? (
          <ChartGroupsUI.GroupItemIcon
            $color={sliceColor}
            $disabled={!displayedSlice.isVisible}
          />
        ) : (
          <ChartGroupsUI.PathSliceIconImage
            index={sliceIndex}
            active={displayedSlice.isVisible}
            width={16}
            height={16}
          />
        )}
        <ChartGroupsUI.GroupName>{displayName}</ChartGroupsUI.GroupName>
        {displayedSlice.hasUserAlias && (
          <RenamedIndicator isDisabled={!displayedSlice.isVisible} />
        )}
      </ChartGroupsUI.SingleValueLabel>
    );
  };

  const builderInfoDisplay = useBuilderInfoDisplay(analysisId);

  const visibleSlices = useMemo(() => {
    const trailingVisibleSlices = displayedSlices
      .slice(topBreakdownsValue)
      .filter((slice) => slice.isVisible);

    const defaultDisplayedSlices = displayedSlices.slice(0, topBreakdownsValue);
    return [...defaultDisplayedSlices, ...trailingVisibleSlices];
  }, [displayedSlices, topBreakdownsValue]);

  const shouldWrap = useMemo(() => {
    if (ref.current) {
      const computeNumberOfRows = () => {
        const computedStyle = window.getComputedStyle(ref.current);
        const gridTemplateRows =
          computedStyle.getPropertyValue('grid-template-rows');
        return gridTemplateRows.split(' ').length;
      };

      const numRows = computeNumberOfRows();
      if (numRows) {
        return (
          visibleSlices.length <= CHART_GROUPS_DEFAULT_COUNT && numRows === 1
        );
      }
    }
  }, [ref, visibleSlices]);

  return (
    <ChartGroupsUI.Container>
      {!isNil(maxGroupsAllowed) && (
        <ChartGroupsUI.GroupbyHint>
          Maximum {maxGroupsAllowed} groups allowed
        </ChartGroupsUI.GroupbyHint>
      )}
      {builderInfoDisplay && (
        <ChartGroupsUI.BuilderInfoLabel>
          {builderInfoDisplay}
        </ChartGroupsUI.BuilderInfoLabel>
      )}
      <ChartGroupsUI.Grid
        className={chartGroupsContainerId}
        ref={ref}
        $shouldWrap={shouldWrap}
      >
        {visibleSlices.map((slice, index) => {
          // if hint is undefined, or hint array is empty, then hide hint
          const tooltipDisabled = !slice.hint || slice.hint.length === 0;
          const tooltipContent = tooltipDisabled
            ? null
            : { content: generateGroupbyHint(slice.hint, slice.rank) };

          const options = getMenuOptions({
            sliceKey: slice.key,
            visibleSlices,
            displayedSlices,
          });

          return (
            <ChartGroupWrapper slice={slice} key={slice.key + '-' + index}>
              <GroupDropdown
                options={options}
                selectedKey={slice.key}
                properties={['key', 'name']}
                singleValueLabel={getGroupLabelElement(slice, index)}
                onChange={(option) => onGroupChange(option, slice.key)}
                tooltipOption={tooltipContent}
                isDisabled={!slice.isVisible}
              />
            </ChartGroupWrapper>
          );
        })}
      </ChartGroupsUI.Grid>
    </ChartGroupsUI.Container>
  );
};
